/** Microsoft Clarity project ID (public). */
export const MICROSOFT_CLARITY_PROJECT_ID = 'wj7mbuohe6';

/**
 * Install Clarity stub + loader script (same behavior as clarity.ms snippet).
 * Safe to call multiple times; second call no-ops.
 */
export function loadMicrosoftClarity(): void {
	if (typeof window === 'undefined' || typeof document === 'undefined') return;
	const id = MICROSOFT_CLARITY_PROJECT_ID;
	if (document.querySelector(`script[src*="clarity.ms/tag/${id}"]`)) return;

	const c = window as Window & { clarity?: ClarityQueueFn };
	const a = 'clarity';

	if (typeof c[a] !== 'function') {
		const clarityStub = function clarityStub(...args: unknown[]) {
			clarityStub.q ??= [];
			clarityStub.q.push(args);
		} as ClarityQueueFn;
		clarityStub.q = [];
		c[a] = clarityStub;
	}

	const t = document.createElement('script');
	t.async = true;
	t.src = `https://www.clarity.ms/tag/${id}`;
	const y = document.getElementsByTagName('script')[0];
	y?.parentNode?.insertBefore(t, y);
}

type ClarityQueueFn = ((...args: unknown[]) => void) & {
	q?: unknown[][];
};

function callClarity(...args: unknown[]): void {
	const c = window as Window & { clarity?: ClarityQueueFn };
	if (typeof c.clarity !== 'function') return;
	c.clarity(...args);
}

/**
 * Fire a Clarity custom event, optionally tagging the session for segmentation.
 *
 * No-ops until the Clarity script is installed — analytics load is deferred and
 * gated on cookie consent — so callers never need to guard the call.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/custom-events
 */
export function trackClarityEvent(name: string, tags?: Record<string, string>): void {
	if (typeof window === 'undefined') return;

	for (const [key, value] of Object.entries(tags ?? {})) {
		if (value) callClarity('set', key, value);
	}

	callClarity('event', name);
}

/** Apply consent from stored preference (no revoke / cookie wipe). */
export function syncClarityConsentFromChoice(accepted: boolean): void {
	if (accepted) {
		callClarity('consentv2', {
			ad_Storage: 'granted',
			analytics_Storage: 'granted',
		});
	} else {
		callClarity('consentv2', {
			ad_Storage: 'denied',
			analytics_Storage: 'denied',
		});
	}
}

/**
 * User declined cookies — deny storage and clear Clarity cookies per MS docs.
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
 */
export function revokeClarityConsent(): void {
	syncClarityConsentFromChoice(false);
	callClarity('consent', false);
}
